import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLicensingContext } from "../../context/useLicensingContext";
import { useProjectContext } from "../../context/useProjectContext";
import { PRODUCTS, ROUTE_MAP, DefaultIcon, CARD_COLOR, CARD_LIGHT_COLOR, CARD_BORDER_COLOR } from "../../constants/productConstants";

const NoSubscription = () => {
  const navigate = useNavigate();
  const { getPlatformProducts } = useLicensingContext();
  const { selectedProject } = useProjectContext();
  const [products, setProducts] = useState(null);

  useEffect(() => {
    if (!selectedProject?.id) return;

    getPlatformProducts(
      selectedProject.id,
      (response) => {
        const apiProducts = response?.data?.products || [];

        const merged = apiProducts
          .filter((p) => p.active)
          .map((p) => {
            const defaults = PRODUCTS[p.slug] || {};
            const meta = p.display_metadata || {};

            const firstPlan = p.product_plans?.[0];
            const planBenefits = firstPlan?.display_metadata?.benefits || [];

            return {
              key: p.slug,
              title: defaults.title || p.name,
              route: ROUTE_MAP[p.slug],
              description: defaults.description,
              icon: defaults.icon ? defaults.icon() : <DefaultIcon />,
              features: defaults.features || [],
            };
          });

        setProducts(
          merged.length > 0
            ? merged
            : Object.values(PRODUCTS).map((d, i) => ({
                key: Object.keys(PRODUCTS)[i],
                ...d,
              }))
        );
      },
      () => {
        setProducts(
          Object.entries(PRODUCTS).map(([key, d]) => ({ key, ...d }))
        );
      }
    );
  }, [selectedProject?.id]);

  const displayProducts =
    products ||
    Object.entries(PRODUCTS).map(([key, d]) => ({ key, ...d }));

  return (
    <Wrapper>
      <HeroSection>
        <HeroText>
          <HeroTitle>Get started with emotii.ai</HeroTitle>
          <HeroSubtitle>
            You don't have an active subscription yet. Unlock real-time AI interpretation across all your products.
          </HeroSubtitle>
        </HeroText>
      </HeroSection>

      <Divider>
        <DividerLine />
        <DividerText>What's included</DividerText>
        <DividerLine />
      </Divider>

      <ProductsGrid count={displayProducts.length}>
        {displayProducts.map((p, i) => (
          <ProductCard key={`${p.key}-${i}`} style={{ animationDelay: `${i * 100}ms` }}>
            <ProductCardTop>
              <IconWrapper>{p.icon}</IconWrapper>
              <ProductTitle>{p.title}</ProductTitle>
            </ProductCardTop>

            <ProductDescription>{p.description}</ProductDescription>

            {p.features.length > 0 && (
              <FeatureList>
                {p.features.map((f) => (
                  <FeatureItem key={f}>
                    <FeatureDot />
                    {f}
                  </FeatureItem>
                ))}
              </FeatureList>
            )}

            <ProductCTA onClick={() => navigate(p.route)}>
              Learn more <ArrowRight size={13} strokeWidth={2.5} />
            </ProductCTA>
          </ProductCard>
        ))}
      </ProductsGrid>
    </Wrapper>
  );
};

export default NoSubscription;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  animation: ${fadeUp} 0.4s ease both;
`;

const HeroSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 24px;
  padding: 20px 24px;
  background: #fafafa;
  border: 1px solid #f2f4f7;
  border-radius: 20px;
`;

const HeroText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 520px;
`;

const HeroTitle = styled.h2`
  color: #101828;
  font-size: 26px;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.02em;
`;

const HeroSubtitle = styled.p`
  color: #667085;
  font-size: 15px;
  line-height: 1.6;
  margin: 0;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: #e9eaeb;
`;

const DividerText = styled.span`
  color: #98a2b3;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  white-space: nowrap;
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: ${(p) =>
    p.count === 1
      ? "minmax(300px, 500px)"
      : p.count === 2
      ? "repeat(2, 1fr)"
      : "repeat(3, 1fr)"};
  gap: 20px;

  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

const ProductCard = styled.div`
  background: #ffffff;
  border: 1px solid ${CARD_BORDER_COLOR};
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.05);
  animation: ${fadeUp} 0.4s ease both;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 24px rgba(127, 86, 217, 0.1);
  }
`;

const ProductCardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  min-width: 48px;
  border-radius: 12px;
  background: ${CARD_LIGHT_COLOR};
  border: 1px solid ${CARD_BORDER_COLOR};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProductTitle = styled.h3`
  color: ${CARD_COLOR};
  font-size: 16px;
  font-weight: 700;
  margin: 0;
`;

const ProductDescription = styled.p`
  color: #667085;
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
`;

const FeatureList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #344054;
  font-size: 13px;
  font-weight: 500;
`;

const FeatureDot = styled.div`
  width: 6px;
  height: 6px;
  min-width: 6px;
  border-radius: 50%;
  background: ${CARD_COLOR};
`;

const ProductCTA = styled.button`
  margin-top: auto;
  background: ${CARD_LIGHT_COLOR};
  color: ${CARD_COLOR};
  border: 1px solid ${CARD_BORDER_COLOR};
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: background 0.2s, transform 0.15s;

  &:hover {
    background: #ede9fe;
    transform: translateY(-1px);
  }
`;